// storage.js - LocalStorage wrapper for game state persistence

export class Storage {
    constructor() {
        this.storageKey = 'taskagotchi_save';
        this.defaultState = this.getDefaultState();
    }

    getDefaultState() {
        return {
            player: {
                id: 'local_' + Date.now(),
                name: 'Player',
                cosmetics: ['default_skin'],
                score: 0,
                wellness: 80,
                unlocks: [],
                deaths: 0,
                tasksCompleted: 0,
                tasksAttempted: 0,
                playTime: 0
            },
            house: {
                currentFloor: 'house',
                currentRoom: 'hallway',
                lastSaved: new Date().toISOString(),
                visitedRooms: ['hallway'],
                unlockedRooms: ['kitchen', 'hallway', 'study', 'lab']
            },
            tasks: {
                available: [],
                completed: [],
                failed: [],
                current: null,
                lastTaskTime: null,
                nextTaskTime: null
            },
            settings: {
                musicEnabled: true,
                musicVolume: 70,
                sfxEnabled: true,
                sfxVolume: 70,
                highContrast: false,
                tutorialComplete: false
            },
            progression: {
                level: 1,
                experience: 0,
                trophies: [],
                streakDays: 0,
                lastPlayed: new Date().toISOString()
            }
        };
    }

    // Load game state from localStorage
    load() {
        try {
            const savedData = localStorage.getItem(this.storageKey);
            if (savedData) {
                const parsed = JSON.parse(savedData);
                // Merge with defaults to handle new properties in updates
                return this.mergeWithDefaults(parsed);
            }
        } catch (error) {
            console.error('Error loading save data:', error);
        }
        return this.defaultState;
    }

    // Save game state to localStorage
    save(state) {
        try {
            state.house.lastSaved = new Date().toISOString();
            state.progression.lastPlayed = new Date().toISOString();
            localStorage.setItem(this.storageKey, JSON.stringify(state));
            return true;
        } catch (error) {
            console.error('Error saving game:', error);
            return false;
        }
    }

    // Check if a save exists
    hasSave() {
        return localStorage.getItem(this.storageKey) !== null;
    }

    // Delete save data
    deleteSave() {
        try {
            localStorage.removeItem(this.storageKey);
            return true;
        } catch (error) {
            console.error('Error deleting save:', error);
            return false;
        }
    }

    // Merge saved data with defaults (for version compatibility)
    mergeWithDefaults(saved) {
        const merged = JSON.parse(JSON.stringify(this.defaultState));

        // Deep merge function
        const deepMerge = (target, source) => {
            for (const key in source) {
                if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
                    if (!target[key]) target[key] = {};
                    deepMerge(target[key], source[key]);
                } else {
                    target[key] = source[key];
                }
            }
            return target;
        };

        return deepMerge(merged, saved);
    }

    // Export save data as JSON (for backup)
    exportSave() {
        const state = this.load();
        const dataStr = JSON.stringify(state, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });

        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `taskagotchi_save_${Date.now()}.json`;
        link.click();
        URL.revokeObjectURL(url);
    }

    // Import save data from JSON file
    importSave(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const imported = JSON.parse(e.target.result);
                    const merged = this.mergeWithDefaults(imported);
                    this.save(merged);
                    resolve(merged);
                } catch (error) {
                    reject(error);
                }
            };
            reader.onerror = reject;
            reader.readAsText(file);
        });
    }

    // Calculate wellness decay based on time since last play
    calculateWellnessDecay(state) {
        const lastPlayed = new Date(state.progression.lastPlayed);
        const now = new Date();
        const hoursSince = (now - lastPlayed) / (1000 * 60 * 60);

        // Decay rate: 2 wellness per hour (configurable)
        const decayRate = 2;
        const decay = Math.floor(hoursSince * decayRate);

        return Math.min(decay, state.player.wellness); // Don't go below 0
    }

    // Apply wellness decay when loading a save
    applyWellnessDecay(state) {
        const decay = this.calculateWellnessDecay(state);
        state.player.wellness = Math.max(0, state.player.wellness - decay);

        if (decay > 0) {
            console.log(`Wellness decayed by ${decay} points since last play`);
        }

        return state;
    }
}

// Create singleton instance
export const storage = new Storage();
